import { useCallback } from "react";

import { EventResizeDoneArg } from "@fullcalendar/interaction";
import { EventDropArg } from "@fullcalendar/react";

import { Activity } from "@mapmycustomers/shared/types/entity";
import User from "@mapmycustomers/shared/types/User";

import getActivityAllowedUsers from "@app/util/activity/getActivityAllowedUsers";

import { default as CalendarEventObject } from "../component/ActivityCalendarPage/type/EventObject";
import { default as SchedulerEventObject } from "../component/ActivitySchedulerPage/type/EventObject";

const useEventDropResize = (
  currentUserId: User["id"],
  onMoveActivity: (payload: {
    activity: Activity;
    failCallback?: (activity: Activity) => void;
  }) => void,
  users: User[],
  isReadOnlyMember?: boolean
) =>
  useCallback(
    (e: EventDropArg | EventResizeDoneArg) => {
      if (!e.event.extendedProps?.activity || isReadOnlyMember) {
        e.revert(); // you can only drag activities anyway
        return;
      }
      const activity = (e.event.extendedProps as CalendarEventObject | SchedulerEventObject)
        .activity!;

      const allowedUsers = getActivityAllowedUsers(
        currentUserId,
        users,
        activity.userIds ?? [],
        activity.visibility,
        activity.teamId ?? [],
        activity?.assignee
      );

      if (
        "newResource" in e &&
        e.newResource &&
        !allowedUsers.includes(e.newResource.extendedProps.user.username)
      ) {
        e.revert();
        return;
      }

      const updatedActivity: Activity = {
        ...activity,
        allDay: e.event.allDay,
        // @ts-ignore
        assigneeId: e.newResource ? e.newResource.extendedProps.user.id : activity.assignee.id,
        endAt: e.event.end?.toISOString() ?? null,
        startAt: e.event.start?.toISOString() ?? null,
      };
      onMoveActivity({ activity: updatedActivity, failCallback: () => e.revert() });
    },
    [currentUserId, isReadOnlyMember, onMoveActivity, users]
  );

export default useEventDropResize;
